'use client';
import { CustomTooltip } from '@/components/custom-tooltip';
import { TurkeyIcon } from '@/components/icons/TurkeyIcon';
import { Button } from '@/components/ui/button';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import type { GetAskRevQueryResult } from '@/graphql/gql-types';
import { cn } from '@/lib/utils';
import { shouldShowTurkey } from '@/utils/date-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from 'common/_variables.module.scss';
import {
	ArrowDownAZ,
	ArrowUpAZ,
	Check,
	Info,
	RotateCcw,
	Send,
	Speech,
	X,
} from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

type AskRevProps = {
	submitQuery: Function;
	querySummary: string;
	compSet?: Array<{ hotelId: number; name: string }>;
	value?: string;
	onValueChange?: (value: string) => void;
	suggestion?: string;
	sortBy?: string;
	sortDirection?: 'asc' | 'desc' | '';
	queryHistoryData: {
		getAskRevQueryHistory?: GetAskRevQueryResult[];
	};
	onClear?: () => void;
	// NEW: AI sorting parameters for display
	aiSortBy?: string | null;
	aiSortDirection?: 'asc' | 'desc' | null;
	aiSortLimit?: number | null;
};
const schema = z.object({
	query: z.string().min(3, 'Please enter a meaningful query.'),
	suggestion: z.string().optional(),
	summary: z.string().optional(),
});
type AskRevFormValues = z.infer<typeof schema>;
// Base example queries
const baseExampleQueries = [
	{ value: '1', label: 'Show nights when my hotel is projected to sell out' },
	{
		value: '2',
		label:
			'Display all nights for the next 2 weeks when my hotel is priced below Market Rate',
	},
	{ value: '3', label: 'List dates with occupancy above 80%' },
	{ value: '4', label: 'Show me dates with low availability' },
	{ value: '5', label: 'What are the weekends with low occupancy?' },
	{
		value: '6',
		label: 'Show me dates when my hotel is the lowest in the market',
	},
	{ value: '7', label: 'What dates have the highest booking pace?' },
	{ value: '8', label: 'Show dates with lowest booking pace' },
	{ value: '9', label: 'Show dates with recs to review' },
	{ value: '10', label: 'Find weekend dates with occupancy below 70%' },
	{
		value: '11',
		label: 'Show dates where my recommended rate is above market rate',
	},
	// NEW: Sorting examples
	{ value: '12', label: 'Show me the top 10 dates with highest ADR' },
	{
		value: '13',
		label: 'Display dates sorted by occupancy percentage, lowest first',
	},
	{ value: '14', label: 'Show the top 5 dates with highest RevPAR' },
	{
		value: '15',
		label: 'List dates with ADR above market rate, sorted by date',
	},
];
const placeholder = 'Ask BestREV a question...';
const searchPlaceholder = 'Search queries...';
export const AskRev = ({
	submitQuery,
	querySummary,
	value,
	compSet,
	onValueChange,
	suggestion,
	queryHistoryData,
	onClear: onClearCallback,
	aiSortBy,
	aiSortDirection,
	aiSortLimit,
}: AskRevProps) => {
	const currentToastRef = React.useRef<string | number | null>(null);
	const [open, setOpen] = React.useState(false);
	const [inputValue, setInputValue] = React.useState(value || '');

	// Transform API data to pastQueries format
	const pastQueries = React.useMemo(() => {
		if (!queryHistoryData?.getAskRevQueryHistory) return [];
		// Deduplicate by query_text to avoid showing the same query multiple times
		const seen = new Set<string>();
		return queryHistoryData.getAskRevQueryHistory
			.filter((query) => {
				if (seen.has(query.query_text || '')) {
					return false;
				}
				seen.add(query.query_text || '');
				return true;
			})
			.map((query, index: number) => ({
				value: `history-${index}`,
				label: query.query_text || '',
				summary: query.summary || '',
			}));
	}, [queryHistoryData]);

	// Helper function to format sort field names for display
	const formatSortFieldName = (sortBy: string) => {
		const fieldMappings: Record<string, string> = {
			adr: 'ADR',
			adr_live: 'ADR (Live)',
			current_rate: 'Current Rate',
			current_rate_live: 'Current Rate (Live)',
			rec_rate: 'Recommended Rate',
			occ_pct: 'Occupancy %',
			occ_pct_live: 'Occupancy % (Live)',
			fcst_occ_pct: 'Forecast Occupancy %',
			current_revenue: 'Current Revenue',
			current_revenue_live: 'Current Revenue (Live)',
			revpar: 'RevPAR',
			revpar_live: 'RevPAR (Live)',
			stay_date: 'Stay Date',
			priority: 'Priority',
			market_rate: 'Market Rate',
		};

		// Handle competitor rate fields (compRates.{hotelId})
		const compRateMatch = sortBy.match(/^compRates\.(\d+)$/);
		if (compRateMatch) {
			const hotelId = compRateMatch[1];
			const competitor = compSet?.find(
				(comp) => comp.hotelId === Number.parseInt(hotelId),
			);
			return competitor
				? `${competitor.name} Rate`
				: `Competitor ${hotelId} Rate`;
		}

		return fieldMappings[sortBy] || sortBy;
	};

	// Create dynamic example queries including competitor-specific ones
	const exampleQueries = React.useMemo(() => {
		const queries = [...baseExampleQueries];

		// Add a dynamic competitor query if we have competitors
		if (compSet && compSet.length > 0) {
			const firstCompetitor = compSet[0];
			queries.push({
				value: `comp-soldout-${firstCompetitor.hotelId}`,
				label: `Show me days where ${firstCompetitor.name} is sold out`,
			});
		}
		return queries;
	}, [compSet]);

	const showQueryExplanation = useCallback(
		(query: string) => {
			if (currentToastRef.current) {
				toast.dismiss(currentToastRef.current);
				currentToastRef.current = null;
			} else {
				currentToastRef.current = toast(
					<div className='tw-relative tw-contents tw-items-start tw-gap-3 !tw-w-full tw-rounded-lg tw-bg-white tw-p-2'>
						<div className='tw-flex-1 tw-pr-10'>
							<div className='tw-font-bold tw-text-sm tw-text-gray-900 tw-mb-1'>
								Query Summary:
							</div>
							<div className='tw-text-sm tw-text-gray-600 tw-leading-relaxed tw-break-words'>
								{query}
							</div>
						</div>
						{suggestion && (
							<div className='tw-mt-8 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg'>
								<div className='tw-flex tw-items-start tw-gap-3'>
									<div className='tw-flex-shrink-0 tw-mt-0.5'>
										<div className='tw-flex tw-items-center'>
											<Info size={20} className='tw-text-yellow-600' />
										</div>
									</div>
									<div className='tw-flex-1'>
										<div className='tw-space-y-1'>
											<h5 className='tw-font-semibold tw-text-sm tw-text-yellow-800'>
												Suggestion
											</h5>
											<p className='tw-text-sm tw-text-yellow-700'>
												{suggestion}
											</p>
										</div>
									</div>
								</div>
							</div>
						)}

						<Button
							variant='ghost'
							size='sm'
							onClick={() => {
								if (currentToastRef.current) {
									toast.dismiss(currentToastRef.current);
									currentToastRef.current = null;
								}
							}}
							className='neena tw-absolute tw-top-0 tw-right-0 tw-z-10 tw-h-6 tw-w-6 hover:tw-bg-gray-100 tw-rounded-full'
						>
							<X className='tw-text-gray-400' />
						</Button>
					</div>,
					{
						duration: Number.POSITIVE_INFINITY,
						closeButton: false,
						position: 'top-right',
						className:
							'tw-w-full tw-max-w-2xl tw-mt-16 !tw-border-2 !tw-border-primary', // Increased max width and added green border
					},
				);
			}
		},
		[suggestion],
	);

	// Update display summary when querySummary prop changes
	useEffect(() => {
		if (querySummary?.trim()) {
			showQueryExplanation(querySummary);
		}
	}, [querySummary, showQueryExplanation]);

	// Cleanup toast when component unmounts (e.g., navigating away from page)
	useEffect(() => {
		return () => {
			if (currentToastRef.current) {
				toast.dismiss(currentToastRef.current);
				currentToastRef.current = null;
			}
		};
	}, []);

	const form = useForm<AskRevFormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			query: '',
		},
	});

	const { setValue } = form;
	const onSubmit = (values: AskRevFormValues) => {
		// For form submissions, don't pass selectedFilters as existing filters
		// selectedFilters is just UI state, not "existing filters from history"
		submitQuery(
			values.query.trim(),
			undefined, // Don't pass existing filters for new queries
			values.suggestion,
			values.summary,
		);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setValue('query', newValue);
		onValueChange?.(newValue);
	};
	const clearInput = () => {
		setInputValue('');
		setValue('query', '');
		onValueChange?.('');
	};
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && inputValue.trim()) {
			e.preventDefault();
			setOpen(false);
			form.handleSubmit(onSubmit)();
		}
	};

	const onClear = () => {
		setInputValue('');
		setValue('query', '');
		onValueChange?.('');
		form.reset();
		onClearCallback?.(); // notify parent component to clear filters

		// Dismiss any active toast
		if (currentToastRef.current) {
			toast.dismiss(currentToastRef.current);
			currentToastRef.current = null;
		}
	};
	return (
		<div className='tw-max-w-2xl'>
			<form onSubmit={form.handleSubmit(onSubmit)} className='tw-space-y-4'>
				<div className='tw-flex tw-items-center tw-gap-3'>
					{/* Speech/microphone icon next to the AskRev textbox - shows turkey on Thanksgiving! */}
					{shouldShowTurkey() ? (
						<TurkeyIcon className='tw-h-6 tw-w-6 tw-flex-shrink-0' />
					) : (
						<Speech
							color={styles.colorGreen}
							className='tw-h-6 tw-w-6 tw-text-green-500 tw-flex-shrink-0'
						/>
					)}
					<Popover open={open} onOpenChange={setOpen}>
						<PopoverTrigger asChild>
							<div className='tw-relative tw-flex-1'>
								<Input
									{...form.register('query')}
									value={inputValue}
									onChange={handleInputChange}
									onKeyDown={handleKeyDown}
									onClick={() => {
										// Delay open to next event loop tick — fixes first click issue
										if (!open) {
											setTimeout(() => setOpen(true), 0);
										}
									}}
									placeholder={placeholder}
									className={
										'tw-h-12 tw-text-base tw-border-gray-300 tw-rounded-lg tw-bg-white hover:tw-bg-gray-50 focus:tw-border-gray-400 tw-pr-20'
									}
									autoComplete='off'
								/>
								<div className='tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center tw-gap-1'>
									{inputValue && (
										<Button
											variant='ghost'
											size='sm'
											onClick={clearInput}
											className='tw-h-8 tw-w-8 tw-p-0 hover:tw-bg-gray-100'
										>
											<X className='tw-h-4 tw-w-4 tw-text-gray-400' />
										</Button>
									)}
								</div>
							</div>
						</PopoverTrigger>
						<PopoverContent
							className='tw-w-[530px] !tw-p-0 tw-bg-white tw-border tw-border-gray-200 tw-shadow-lg tw-z-50'
							align='start'
							sideOffset={2}
							onOpenAutoFocus={(e) => e.preventDefault()}
							onInteractOutside={(e) => {
								// ignore when interacting with the input
								if ((e.target as HTMLElement).closest('input'))
									e.preventDefault();
							}}
						>
							<Command className='tw-bg-white tw-p-0'>
								<CommandInput
									placeholder={searchPlaceholder}
									className='tw-h-8 tw-text-sm tw-border-0 focus:tw-ring-0 tw-px-3 tw-py-1'
								/>
								<CommandList className='tw-max-h-[300px] tw-bg-white'>
									<CommandEmpty className='tw-py-3 tw-text-center tw-text-gray-500 tw-text-sm'>
										No queries found.
									</CommandEmpty>
									{!queryHistoryData && (
										<CommandGroup
											heading='Recent Queries'
											className='[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:tw-text-sm [&_[cmdk-group-heading]]:tw-font-medium [&_[cmdk-group-heading]]:tw-text-gray-700'
										>
											<div className='tw-px-3 tw-py-2 tw-text-center tw-text-gray-500 tw-text-sm'>
												Loading history...
											</div>
										</CommandGroup>
									)}
									{queryHistoryData && pastQueries.length === 0 && (
										<CommandGroup
											heading='Recent Queries'
											className='[&_[cmdk-group-heading]]:tw-px-3 [&_[cmdk-group-heading]]:tw-py-1 [&_[cmdk-group-heading]]:tw-text-sm [&_[cmdk-group-heading]]:tw-font-bold [&_[cmdk-group-heading]]:tw-text-gray-700'
										>
											<div className='tw-px-3 tw-py-2 tw-text-center tw-text-gray-500 tw-text-sm'>
												No recent queries found.
											</div>
										</CommandGroup>
									)}
									{queryHistoryData && pastQueries.length > 0 && (
										<CommandGroup
											heading='Recent Queries'
											className='[&_[cmdk-group-heading]]:tw-px-3 [&_[cmdk-group-heading]]:tw-py-1 [&_[cmdk-group-heading]]:tw-text-sm [&_[cmdk-group-heading]]:tw-font-bold [&_[cmdk-group-heading]]:tw-text-gray-700'
										>
											{pastQueries.map((item) => (
												<CommandItem
													key={item.value}
													value={item.label}
													onSelect={(currentValue: string) => {
														setInputValue(item.label);
														setValue('query', item.label);
														setValue('summary', item.summary);
														onValueChange?.(currentValue);

														// Find the corresponding history item and set its filters
														const historyItem =
															queryHistoryData?.getAskRevQueryHistory?.find(
																(_, idx: number) =>
																	`history-${idx}` === item.value,
															);
														const suggestion = historyItem?.suggestion || '';
														const summary = historyItem?.summary || '';
														setValue('suggestion', suggestion);
														setValue('summary', summary);
														setOpen(false);
													}}
													className='tw-text-wrap !tw-px-3 !tw-py-1 tw-text-sm tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-800 tw-cursor-pointer tw-transition-colors tw-whitespace-normal tw-leading-relaxed'
												>
													<Check
														className={cn(
															'tw-mr-2 tw-h-4 tw-w-4 tw-text-primary',
															value === item.value
																? 'tw-opacity-100'
																: 'tw-opacity-0',
														)}
													/>
													{item.label}
												</CommandItem>
											))}
										</CommandGroup>
									)}
									{queryHistoryData && baseExampleQueries.length > 0 && (
										<CommandSeparator className='tw-bg-gray-200' />
									)}
									{baseExampleQueries.length > 0 && (
										<CommandGroup
											heading='Recommended'
											className='tw-text-wrap [&_[cmdk-group-heading]]:tw-px-3 [&_[cmdk-group-heading]]:tw-py-1 [&_[cmdk-group-heading]]:tw-text-sm [&_[cmdk-group-heading]]:tw-font-bold [&_[cmdk-group-heading]]:tw-text-gray-700'
										>
											{exampleQueries.map((item) => (
												<CommandItem
													key={item.value}
													value={item.value}
													onSelect={(currentValue: string) => {
														setInputValue(item.label);
														setValue('query', item.label);
														onValueChange?.(currentValue);

														// Clear filters for new recommended queries
														setOpen(false);
													}}
													className='tw-text-wrap !tw-px-3 !tw-py-1 tw-text-sm tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-800 tw-cursor-pointer tw-transition-colors tw-whitespace-normal tw-leading-relaxed'
												>
													<Check
														className={cn(
															'tw-mr-2 tw-h-4 tw-w-4 tw-text-primary',
															value === item.value
																? 'tw-opacity-100'
																: 'tw-opacity-0',
														)}
													/>
													{item.label}
												</CommandItem>
											))}
										</CommandGroup>
									)}
								</CommandList>
							</Command>
						</PopoverContent>
					</Popover>
					<div className='tw-flex tw-gap-2 tw-justify-end'>
						<Button
							type='button'
							onClick={onClear}
							variant='outline'
							className='tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-p-0 tw-border-gray-300 hover:tw-bg-gray-50'
						>
							<RotateCcw className='tw-h-4 tw-w-4' />
						</Button>
						<Button
							type='submit'
							disabled={!inputValue.trim()}
							className='tw-flex tw-items-center tw-justify-center tw-bg-primary tw-text-white tw-h-12 tw-w-12 tw-p-0'
						>
							<Send className='tw-h-4 tw-w-4' />
						</Button>
					</div>
					<CustomTooltip
						placement='bottom'
						backgroundColor='tw-bg-white'
						arrowColor='tw-fill-primary'
						arrow
						borderColor='tw-border-primary'
						content={
							<div className='tw-space-y-2 tw-text-sm tw-text-foreground/80'>
								<p className='tw-font-semibold'>Usage Tips:</p>
								<ul className='tw-space-y-1 tw-list-disc tw-ml-5'>
									<li>Be specific about what you're looking for</li>
									<li>Use keywords like "live", "non-live" data</li>
									<li>
										Try the example queries provided in recommended section
									</li>
								</ul>
							</div>
						}
					>
						<Button
							variant={'ghost'}
							size='sm'
							type='button'
							className='tw-h-8 tw-w-8 tw-p-0 tw-flex-shrink-0'
						>
							<Info size={16} className='tw-text-gray-500' />
						</Button>
					</CustomTooltip>
				</div>
			</form>

			{/* AI Sorting Information (if present) */}
			{aiSortBy && aiSortDirection && (
				<div className='tw-mt-4 tw-p-2 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg'>
					<div className='tw-flex tw-items-start tw-gap-3'>
						<div className='tw-flex-shrink-0 tw-mt-0.5'>
							<div className='tw-flex tw-items-center'>
								{aiSortDirection === 'asc' ? (
									<ArrowUpAZ size={20} className='tw-text-blue-600' />
								) : (
									<ArrowDownAZ size={20} className='tw-text-blue-600' />
								)}
							</div>
						</div>
						<div className='tw-flex-1'>
							<div className='tw-flex tw-items-center tw-gap-2 tw-space-x-2'>
								<h5 className='tw-font-semibold tw-text-sm tw-text-blue-800'>
									Sorting applied:
								</h5>
								<p className='tw-text-sm tw-text-blue-700'>
									{formatSortFieldName(aiSortBy)} (
									{aiSortDirection === 'asc' ? 'Low to High' : 'High to Low'})
									{aiSortLimit && ` • Top ${aiSortLimit} results`}
								</p>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Suggestion (if present) */}
			{suggestion && (
				<div className='tw-mt-4 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg'>
					<div className='tw-flex tw-items-start tw-gap-3'>
						<div className='tw-flex-shrink-0 tw-mt-0.5'>
							<div className='tw-flex tw-items-center'>
								<Info size={20} className='tw-text-yellow-600' />
							</div>
						</div>
						<div className='tw-flex-1'>
							<div className='tw-space-y-1'>
								<h5 className='tw-font-semibold tw-text-sm tw-text-yellow-800'>
									Suggestion
								</h5>
								<p className='tw-text-sm tw-text-yellow-700'>{suggestion}</p>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
